import { useFormikContext } from "formik";
import {
  entryType,
  equipmentStatus,
  formStates,
  ACTION_LOG_ACTION_TYPE,
  ACTION_LOG_CONFIG_BASELINE_ACTION_TYPES
} from "../../../../constants";
import { LabelInnerHeading } from "../../log-book-item-form-dialog/FormsStyled";
import { useSelector } from "react-redux";
import { ItemDialogFormContext } from "../../log-book-item-form-dialog/item-context/context";
import { useContext, useEffect, useRef, useState } from "react";
import ACTION_LOGS_DATA_MODEL from "../../../../utils/constants/actionLogsDataModel";
import { LogBookSelectContext } from "../../log-book-table/logBooksSelectAll/context";
import { OwcCheckbox, OwcListItem, OwcSelectDropdown, OwcTextarea, OwcInput } from "@one/react";
import DATA_MODEL_TABLE from "../../../../utils/constants/dataModelTable";
import ClusterGridTableAddFlow from "../../../../components/shared/ClusterAgGridTableAddFlow";
import { generateID, isData4eye } from "@digitallab/grid-common-components";
import { capitalize, find, isEqual, omit } from "lodash";
import { StatusClusterComponent, subClusterMapping } from "../../log-book-table/LogBookClusterTable";
import omitDeep from "omit-deep-lodash";
import moment from "moment";
import { CoverSheetMainPageContext } from "../../../cover-sheet/cover-sheet-main-page-context/context";
import { ClusterActions } from "../../../../components/shared/ClusterActions";
import { PropagationDisabledBanner } from "./PropagationDisabledBanner";

export const defaultHeadCellProps = {
  headProps: {
    style: {
      display: "flex",
      alignItems: "center"
    }
  },
  cellProps: {
    style: {
      display: "flex",
      alignItems: "center",
      paddingRight: "10px"
    }
  }
};

export const updateOnChangeConditional = ({
  key,
  evDetail,
  data,
  thirdStepTableData,
  setThirdStepTableData,
  updatedStateEquipments,
  originalData,
  previousValue
}) => {
  const inventoryId = data?.inventoryId;
  const is4Eyes = isData4eye(data);
  const modifiedData = thirdStepTableData.map((item) => {
    if (item.inventoryId === inventoryId) {
      let shouldPublish = item[key]?.shouldBePublished || false;
      if (is4Eyes) {
        shouldPublish = false;
      } else if (originalData === evDetail || evDetail.length === 0) {
        // RETINA-6521177-08
        shouldPublish = false;
      } else if (originalData === previousValue || previousValue.length === 0) {
        // RETINA-6521177-06
        shouldPublish = true;
      }

      const returnItem = {
        ...item,
        [key]: {
          ...item[key],
          key: evDetail,
          value: evDetail,
          shouldBePublished: shouldPublish
        }
      };
      return returnItem;
    }
    return item;
  });
  setThirdStepTableData(() => modifiedData);
  updatedStateEquipments([...modifiedData]);
};

/**
 * Prepares the conditional field for the cluster grid table
 * @param {string} key
 * @returns {object} field object
 */
const prepareConditionalField = ({
  key,
  thirdStepTableData,
  setThirdStepTableData,
  updatedStateEquipments,
  thirdStepTableDataInitial,
  width
}) => {
  return {
    text: DATA_MODEL_TABLE[key].value,
    sortable: false,
    ...defaultHeadCellProps,
    cellRenderer: prepareTextCellRenderer({
      key,
      thirdStepTableData,
      setThirdStepTableData,
      updatedStateEquipments,
      thirdStepTableDataInitial,
      width
    })
  };
};

/**
 *  function to show value in input fields
 * @param {{key:string,values:string, shouldBePublished:boolean}|string|undefined} value
 * @returns {string|null} value to show
 */
const showValue = (value) => {
  if (typeof value === "object" && value !== null && "key" in value) {
    return value.key;
  }
  return value || null;
};

/**
 * function to prepare cell for fields ot text area type
 * @param {{key:string,thirdStepTableData:object[],setThirdStepTableData:Function,updatedStateEquipments:Function,width:number}} params
 * @returns {JSX.Element} cell renderer
 */
const prepareTextAreaCellRenderer = ({
  key,
  thirdStepTableData,
  setThirdStepTableData,
  updatedStateEquipments,
  thirdStepTableDataInitial,
  width = 255
}) => {
  return (params) => {
    const [isFocused, setIsFocused] = useState(false);
    const originalData = thirdStepTableDataInitial[params.rowIndex]?.[key]?.value;
    const previousValue = params.data?.configurationDetails?.value;
    return (
      <div style={{ margin: 5, display: "flex", alignItems: "center" }}>
        <OwcCheckBoxPropagate
          originalData={thirdStepTableDataInitial[params.rowIndex]?.[key]}
          initialState
          params={params}
          thirdStepTableData={thirdStepTableData}
          setThirdStepTableData={setThirdStepTableData}
          updatedStateEquipments={updatedStateEquipments}
          propKey={key}
        />
        <OwcTextarea
          onFocus={() => setIsFocused(true)}
          onPaste={(e) => {
            e.preventDefault();
            const pastedText = e.clipboardData.getData("text");
            updateOnChangeConditional({
              key,
              evDetail: pastedText,
              data: params.data,
              thirdStepTableData,
              setThirdStepTableData,
              updatedStateEquipments,
              originalData,
              previousValue
            });
          }}
          onBlur={(ev) => {
            setIsFocused(false);
            updateOnChangeConditional({
              key,
              evDetail: ev.target.value,
              data: params.data,
              thirdStepTableData,
              setThirdStepTableData,
              updatedStateEquipments,
              originalData,
              previousValue
            });
          }}
          resizable
          data-testid={`{${key}}-input`}
          rows={isFocused ? 10 : 1}
          columns={50}
          maxLength={isFocused ? 300 : undefined}
          type="text"
          variant="outlined"
          id={`{${key}}`}
          name={`{${key}}`}
          placeholder="-"
          disabled={params.data?.entryType === entryType?.cluster}
          value={showValue(params.data[key])}
          style={{
            width,
            backgroundColor: "white",
            textOverflow: "ellipsis"
          }}
        ></OwcTextarea>
      </div>
    );
  };
};

/**
 * function to prepare cell for fields ot text type
 * @param {{key:string,thirdStepTableData:object[],setThirdStepTableData:Function,updatedStateEquipments:Function,width:number}} params
 * @returns {JSX.Element} cell renderer
 */
const prepareTextCellRenderer = ({
  key,
  thirdStepTableData,
  setThirdStepTableData,
  updatedStateEquipments,
  thirdStepTableDataInitial,
  width = 155
}) => {
  return (params) => {
    const originalData = thirdStepTableDataInitial[params.rowIndex]?.[key]?.value;
    const previousValue = params.data?.[key]?.value;
    return (
      <div style={{ margin: 5, display: "flex", alignItems: "center" }}>
        <OwcCheckBoxPropagate
          originalData={thirdStepTableDataInitial[params.rowIndex]?.[key]}
          initialState
          params={params}
          thirdStepTableData={thirdStepTableData}
          setThirdStepTableData={setThirdStepTableData}
          updatedStateEquipments={updatedStateEquipments}
          propKey={key}
        />
        <OwcInput
          onBlur={(ev) =>
            updateOnChangeConditional({
              key,
              evDetail: ev.target.value,
              data: params.data,
              thirdStepTableData,
              setThirdStepTableData,
              updatedStateEquipments,
              originalData,
              previousValue
            })
          }
          data-testid={`{${key}}-input`}
          type="text"
          variant="outlined"
          id={`{${key}}`}
          name={`{${key}}`}
          disabled={params.data?.entryType === entryType?.cluster}
          placeholder="-"
          value={showValue(params.data[key])}
          style={{
            margin: 5,
            width,
            backgroundColor: "white",
            textOverflow: "ellipsis"
          }}
        ></OwcInput>
      </div>
    );
  };
};

/**
 * function to create checkoxes for fields in cluster table
 * @param {{initialState:boolean,params:object,thirdStepTableData:object[],setThirdStepTableData:Function,updatedStateEquipments:Function,propKey:string}} param0
 * @returns {JSX.Element}
 */
const OwcCheckBoxPropagate = ({
  initialState,
  originalData,
  params,
  thirdStepTableData,
  setThirdStepTableData,
  updatedStateEquipments,
  propKey
}) => {
  const is4Eyes = isData4eye(params.data);
  const shouldPropagate =
    params.data?.[propKey]?.shouldBePublished !== undefined ? params.data?.[propKey]?.shouldBePublished : false;

  const currentValue = params.data?.[propKey]?.value;
  const hasValueChanged = originalData?.value !== currentValue;
  const isValueEmpty = currentValue === null || currentValue === undefined || currentValue === "";
  initialState = !is4Eyes;

  const onChange = (checkboxState) => {
    const shouldBePublished = checkboxState;
    const inventoryId = params.data?.inventoryId;
    const modifiedData = thirdStepTableData.map((item) => {
      if (item.inventoryId === inventoryId) {
        const oldItem =
          typeof item[propKey] === "object" ? { ...item[propKey] } : { key: item[propKey], value: item[propKey] };
        return {
          ...item,
          [propKey]: { ...oldItem, shouldBePublished }
        };
      }
      return item;
    });
    setThirdStepTableData(() => modifiedData);
    updatedStateEquipments([...modifiedData]);
  };
  return (
    <OwcCheckbox
      checked={shouldPropagate}
      disabled={is4Eyes || !hasValueChanged || isValueEmpty}
      onInputChange={(e) => onChange(e.target.checked)}
    ></OwcCheckbox>
  );
};

const ActionDataInputsBoxClusterImpact = ({ equipmentDetail }) => {
  const formik = useFormikContext();

  const gxpReadys = useSelector((store) => store.runLogsForm.gxpReadys);
  const systemStatuses = useSelector((store) => store.runLogsForm.systemStatuses);
  //subClusterPendingFlag is set in ActionDataInbutBoxCluster.jsx
  const { setSubClusterFlag, setIsSubmitDisabled, setPropagationSync, subClusterPendingFlag } =
    useContext(ItemDialogFormContext);
  const {
    selected,
    onSetSelection,
    selectedOnlyParents,
    onAddSelectedParents,
    thirdStepTableDataInitial,
    setThirdStepTableDataInitial
  } = useContext(LogBookSelectContext);
  const [thirdStepTableData, setThirdStepTableData] = useState([]);
  const currentTableData = useRef([]);
  const { formState } = useContext(CoverSheetMainPageContext);
  const [isSubmitDisabledExist, setSubmitDisabledExist] = useState(false);
  const [isSubSystemStatusChange, setIsSubSystemStatusChange] = useState(false);
  const [isSubQualificationStatusChange, setIsSubQualificationStatusChange] = useState(false);
  const [isPropagationDisabled, setIsPropagationDisabled] = useState(false);
  const updateReduxKeys = (updatedDetail) => {
    let tempDetail;
    updatedDetail = omitDeep(
      updatedDetail,
      "__typename",
      "id",
      "approverId",
      "runLogEntryId",
      "logSheetEntryId",
      "approverUserName",
      "instrumentDetails"
    );
    tempDetail = {
      ...updatedDetail
    };
    return tempDetail;
  };

  const updateFormikKeys = (updatedDetail) => {
    let tempDetail;
    updatedDetail = omitDeep(
      updatedDetail,
      "__typename",
      "editReason",
      "editComment",
      "runLogEntryId",
      "logSheetEntryId",
      "id",
      "approverId",
      "approverUserName",
      "instrumentDetails"
    );
    tempDetail = {
      ...updatedDetail,
      actionDate: (updatedDetail.actionDate =
        moment(updatedDetail?.actionDate).format("YYYY-MM-DD") || updatedDetail.actionDate),
      ecrNumber: updatedDetail?.ecrNumber || null,
      gxpReady: omit(updatedDetail?.gxpReady, "site"),
      gxpReadyCluster: omit(updatedDetail?.gxpReadyCluster, "site"),
      systemStatus: omit(updatedDetail?.systemStatus, "site"),
      systemStatusCluster: omit(updatedDetail?.systemStatusCluster, "site")
    };
    return tempDetail;
  };

  useEffect(() => {
    if (formState === formStates.EDITABLE_FORM) {
      let tempequipmentDetail = updateReduxKeys(equipmentDetail);
      let tempFormikValues = updateFormikKeys(formik?.values);
      if (!isEqual(tempequipmentDetail, tempFormikValues)) {
        setSubmitDisabledExist(() => true);
      }
      setIsSubmitDisabled(() => isEqual(tempequipmentDetail, tempFormikValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values]);

  useEffect(() => {
    const flatSubEquipments = subClusterMapping(equipmentDetail?.subEquipment);
    let onlyParentEquipments = [];
    let selectedEquipments = [...selected];

    selectedEquipments.forEach((item) => {
      if (item?.clusterId) {
        let parentObj = find(flatSubEquipments, {
          inventoryId: item?.clusterId
        });
        if (parentObj && !find(selectedEquipments, { inventoryId: parentObj?.inventoryId })) {
          const existingObj = find(selectedOnlyParents, {
            inventoryId: parentObj?.inventoryId
          });
          onlyParentEquipments.push(existingObj ? existingObj : parentObj);
        }
      }
    });
    onAddSelectedParents(onlyParentEquipments);
    currentTableData.current = [...selected, ...selectedOnlyParents];

    // showing warning banner if 4-eyes equipment is present in sub-equipments
    const is4EpInSubEquipment = flatSubEquipments.some((item) => isData4eye(item));
    if (is4EpInSubEquipment) {
      setIsPropagationDisabled(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const normalizeField = (val) =>
      typeof val === "string" || !val ? { key: val || "", value: val || "", shouldBePublished: false } : val;

    const normalized = [...selected, ...selectedOnlyParents].map((item) => ({
      ...item,
      configurationBaseline: normalizeField(item.configurationBaseline),
      softwareVersion: normalizeField(item.softwareVersion),
      configurationDetails: normalizeField(item.configurationDetails)
    }));
    setThirdStepTableData(() => normalized);
  }, [selected, selectedOnlyParents]);

  useEffect(() => {
    formik.setFieldValue("gxpReady", formik.values?.gxpReadyCluster);
    formik.setFieldValue("systemStatus", formik.values?.systemStatusCluster);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.gxpReadyCluster, formik.values?.systemStatusCluster]);

  useEffect(() => {
    if (
      thirdStepTableData.length > 0 &&
      (thirdStepTableDataInitial.length === 0 || thirdStepTableDataInitial.length !== thirdStepTableData.length)
    ) {
      setThirdStepTableDataInitial([...thirdStepTableData]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdStepTableData]);

  useEffect(() => {
    const hasSystemStatusChangedInTable = thirdStepTableData.some(
      (item, index) => item?.systemStatus?.value !== thirdStepTableDataInitial[index]?.systemStatus?.value
    );

    const hasGxpReadyChangedInTable = thirdStepTableData.some(
      (item, index) => item?.gxpReady?.value !== thirdStepTableDataInitial[index]?.gxpReady?.value
    );

    const hasSystemStatusChangedInForm =
      formik.values?.systemStatusCluster?.shouldBePublished &&
      formik.values?.systemStatusCluster?.newValue?.value !== equipmentDetail.systemStatus;

    const hasGxpReadyChangedInForm =
      formik.values?.gxpReadyCluster?.shouldBePublished &&
      formik.values?.gxpReadyCluster?.newValue?.value?.toUpperCase() !== equipmentDetail?.qualificationStatus;

    const hasSoftwareVersionChanged =
      formik.values?.updatedSoftwareVersion?.newValue &&
      formik.values?.updatedSoftwareVersion?.newValue !== equipmentDetail?.softwareVersion;

    const systemDiff = hasSystemStatusChangedInTable || hasSystemStatusChangedInForm;
    const gxpDiff = hasGxpReadyChangedInTable || hasGxpReadyChangedInForm;

    if (subClusterPendingFlag) {
      setPropagationSync(5);
    } else if (systemDiff && gxpDiff && hasSoftwareVersionChanged) {
      setPropagationSync(4);
    } else if (systemDiff && gxpDiff) {
      setPropagationSync(3);
    } else if (systemDiff) {
      setPropagationSync(2);
    } else if (gxpDiff) {
      setPropagationSync(1);
    } else {
      setPropagationSync(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values?.gxpReadyCluster,
    formik.values?.systemStatusCluster,
    formik.values?.updatedSoftwareVersion,
    thirdStepTableData,
    thirdStepTableDataInitial,
    subClusterPendingFlag
  ]);

  useEffect(() => {
    let gxpReadyStatus = false;
    let systemStatus = false;
    thirdStepTableData?.forEach((item) => {
      if (item?.gxpReady?.key === null || item?.gxpReady?.key === undefined) {
        gxpReadyStatus = true;
      }
      if (item?.systemStatus?.key === null || item?.systemStatus?.key === undefined) {
        systemStatus = true;
      }
      if (item?.systemStatus?.key === null || item?.systemStatus?.key === undefined) {
        systemStatus = true;
      }
    });
    if (gxpReadyStatus || systemStatus) {
      setSubClusterFlag(true);
    } else {
      setSubClusterFlag(false);
    }

    if (!isSubmitDisabledExist && thirdStepTableData && thirdStepTableData?.length > 0) {
      setIsSubmitDisabled(() => isEqual(currentTableData.current, thirdStepTableData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdStepTableData]);

  const updatedStateEquipments = (modifiedData) => {
    let onlyParentModifiedData = [];
    let selectedData = modifiedData?.filter((item) => {
      let obj = find(selectedOnlyParents, { inventoryId: item?.inventoryId });
      if (obj) {
        onlyParentModifiedData?.push(item);
        return false;
      }
      return true;
    });
    onAddSelectedParents(onlyParentModifiedData);
    onSetSelection(selectedData);
  };

  let conditionalFields = {};

  if (formik.values?.action === ACTION_LOG_ACTION_TYPE.SOFTWARE_CHANGE) {
    conditionalFields[DATA_MODEL_TABLE.softwareVersion.key] = prepareConditionalField({
      key: DATA_MODEL_TABLE.softwareVersion.key,
      thirdStepTableData,
      setThirdStepTableData,
      updatedStateEquipments,
      thirdStepTableDataInitial
    });
  } else if (ACTION_LOG_CONFIG_BASELINE_ACTION_TYPES.includes(formik.values?.action)) {
    conditionalFields[DATA_MODEL_TABLE.configurationBaseline.key] = prepareConditionalField({
      key: DATA_MODEL_TABLE.configurationBaseline.key,
      thirdStepTableData,
      setThirdStepTableData,
      updatedStateEquipments,
      width: 200,
      thirdStepTableDataInitial
    });
  }

  const EquipmentMetaDisplay = {
    fields: {
      clusterIdentifier: {
        headProps: { style: { width: 100 } },
        cellProps: { style: { width: 100 } },
        text: "",
        sortable: false,
        component: ({ item, triggerClusterSetCollapse, client, clusterEquipmentInfoAction }) => {
          switch (item.entryType) {
            case entryType?.cluster:
              return (
                <ClusterActions
                  client={client}
                  item={item}
                  triggerClusterSetCollapse={triggerClusterSetCollapse}
                  clusterEquipmentInfoAction={clusterEquipmentInfoAction}
                  viewExpander={true}
                />
              );
            default:
              return "";
          }
        }
      },
      equipmentModel: {
        text: DATA_MODEL_TABLE.equipmentModel.value,
        sortable: false,
        ...defaultHeadCellProps
      },
      equipmentId: {
        text: DATA_MODEL_TABLE.equipmentId.value,
        sortable: false,
        ...defaultHeadCellProps
      },
      serialNumber: {
        text: DATA_MODEL_TABLE.serialNumber.value,
        sortable: false,
        ...defaultHeadCellProps
      },
      equipmentNickName: {
        text: DATA_MODEL_TABLE.equipmentNickName.value,
        sortable: false,
        ...defaultHeadCellProps
      },
      status: {
        text: DATA_MODEL_TABLE.status.value,
        sortable: false,
        ...defaultHeadCellProps,
        cellRenderer: (params) => {
          return (
            <div style={{ marginTop: "10px" }}>
              <StatusClusterComponent params={params} />
            </div>
          );
        }
      },
      gxpReady: {
        text: ACTION_LOGS_DATA_MODEL.gxpReady.value,
        sortable: true,
        ...defaultHeadCellProps,
        cellRenderer: (params) => {
          const id = generateID.dropDownID("gxpReadys", "dropdown");
          // For cluster entries, force enable the checkbox for 2 fields: gxpReady and systemStatus
          const isCluster = params.data?.entryType === entryType?.cluster;
          return (
            <div style={{ margin: 5, display: "flex", alignItems: "center" }}>
              <OwcCheckBoxPropagate
                originalData={thirdStepTableDataInitial[params.rowIndex]?.[DATA_MODEL_TABLE.gxpReady.key]}
                initialState
                forceEnabled={isCluster}
                params={params}
                thirdStepTableData={thirdStepTableData}
                setThirdStepTableData={setThirdStepTableData}
                updatedStateEquipments={updatedStateEquipments}
                propKey={DATA_MODEL_TABLE.gxpReady.key}
              />
              <OwcSelectDropdown
                outlined
                notFoundText={"No options"}
                style={{ width: 155 }}
                placeholder="Select"
                onSelectChange={(e) => {
                  const originalValue =
                    thirdStepTableDataInitial[params.rowIndex][DATA_MODEL_TABLE.gxpReady.key]?.value;
                  const previousValue = params.data?.gxpReady?.value;

                  let selected = e.detail;
                  selected = selected.map((v) => gxpReadys[v]);
                  const inventoryId = params?.data?.inventoryId;
                  const modifiedData = thirdStepTableData.map((item) => {
                    if (item.inventoryId === inventoryId) {
                      const is4Eyes = isData4eye(item);
                      let shouldBePublished = params.data?.gxpReady?.shouldBePublished || false;
                      if (is4Eyes) {
                        shouldBePublished = false;
                        // RETINA-6521177-08
                      } else if (originalValue === selected[0].value) {
                        shouldBePublished = false;
                        // RETINA-6521177-06
                      } else if (originalValue === previousValue) {
                        shouldBePublished = true;
                      }

                      return {
                        ...item,
                        gxpReady: {
                          shouldBePublished,
                          key: selected[0].key,
                          value: selected[0].value
                        }
                      };
                    }
                    return item;
                  });
                  setThirdStepTableData(() => modifiedData);
                  updatedStateEquipments([...modifiedData]);
                  setIsSubQualificationStatusChange(!isSubQualificationStatusChange);
                }}
                id={id}
                disabled={
                  formState !== formStates.EDITABLE_FORM && params?.data?.status === equipmentStatus?.pending?.key
                }
              >
                {gxpReadys?.map((gxpReadyItem, index) => {
                  return (
                    <OwcListItem
                      key={`${id}-gxpReadyOptionItem-${index}`}
                      selected={gxpReadyItem?.value === params?.data?.gxpReady?.value}
                    >
                      {gxpReadyItem?.value}
                    </OwcListItem>
                  );
                })}
              </OwcSelectDropdown>
            </div>
          );
        }
      },
      systemStatus: {
        text: DATA_MODEL_TABLE.systemStatus.value,
        sortable: true,
        ...defaultHeadCellProps,
        cellRenderer: (params) => {
          const id = generateID.dropDownID("systemStatuses", "dropdown");
          // For cluster entries, force enable the checkbox for 2 fields: gxpReady and systemStatus
          const isCluster = params.data?.entryType === entryType?.cluster;
          return (
            <div style={{ margin: 5, display: "flex", alignItems: "center" }}>
              <OwcCheckBoxPropagate
                originalData={thirdStepTableDataInitial[params.rowIndex]?.[DATA_MODEL_TABLE.systemStatus.key]}
                initialState
                params={params}
                thirdStepTableData={thirdStepTableData}
                setThirdStepTableData={setThirdStepTableData}
                updatedStateEquipments={updatedStateEquipments}
                propKey={DATA_MODEL_TABLE.systemStatus.key}
                forceEnabled={isCluster}
              />
              <OwcSelectDropdown
                outlined
                notFoundText={"No options"}
                style={{ width: 155 }}
                placeholder="Select"
                onSelectChange={(e) => {
                  const originalValue =
                    thirdStepTableDataInitial[params.rowIndex][DATA_MODEL_TABLE.systemStatus.key]?.value;
                  const previousValue = params.data?.systemStatus?.value;

                  let selected = e.detail;
                  selected = selected.map((v) => systemStatuses[v]);
                  const inventoryId = params?.data?.inventoryId;
                  const modifiedData = thirdStepTableData.map((item) => {
                    if (item.inventoryId === inventoryId) {
                      const is4Eyes = isData4eye(item);
                      let shouldBePublished = params.data?.systemStatus?.shouldBePublished || false;
                      if (is4Eyes) {
                        shouldBePublished = false;
                        // RETINA-6521177-08
                      } else if (originalValue === selected[0].value) {
                        shouldBePublished = false;
                        // RETINA-6521177-06
                      } else if (originalValue === previousValue) {
                        shouldBePublished = true;
                      }

                      return {
                        ...item,
                        systemStatus: {
                          shouldBePublished,
                          key: selected[0].key,
                          value: selected[0].value
                        }
                      };
                    }
                    return item;
                  });
                  setThirdStepTableData(() => modifiedData);
                  updatedStateEquipments([...modifiedData]);
                  setIsSubSystemStatusChange(!isSubSystemStatusChange);
                }}
                id={id}
                disabled={
                  formState !== formStates.EDITABLE_FORM && params?.data?.status === equipmentStatus?.pending?.key
                }
              >
                {systemStatuses?.map((statusItem, index) => {
                  return (
                    <OwcListItem
                      key={`${id}-systemStatusOptionItem-${index}`}
                      selected={statusItem?.value === capitalize(params?.data?.systemStatus?.value)}
                    >
                      {statusItem?.value}
                    </OwcListItem>
                  );
                })}
              </OwcSelectDropdown>
            </div>
          );
        }
      },
      ...conditionalFields,
      configurationDetails: {
        text: DATA_MODEL_TABLE.configurationDetails.value,
        sortable: false,
        ...defaultHeadCellProps,
        cellRenderer: prepareTextAreaCellRenderer({
          key: DATA_MODEL_TABLE.configurationDetails.key,
          thirdStepTableData,
          setThirdStepTableData,
          updatedStateEquipments,
          thirdStepTableDataInitial
        }),
        valueGetter: (params) => {
          return showValue(params.data[DATA_MODEL_TABLE.configurationDetails.key]);
        }
      }
    },
    rowKeyId: "id"
  };

  return (
    <>
      <div>
        <LabelInnerHeading>Sub component relevant attributes (check to propagate to repository)</LabelInnerHeading>
      </div>
      {isPropagationDisabled && <PropagationDisabledBanner cluster={true} />}
      <ClusterGridTableAddFlow
        equipmentDetail={equipmentDetail}
        data={thirdStepTableData}
        meta={EquipmentMetaDisplay}
        selected={selected}
      />
    </>
  );
};

export default ActionDataInputsBoxClusterImpact;
